import React, { useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle, Globe } from "lucide-react";
import AuthContext from "../../context/AuthContext";
import usersService from "../../services/usersService";
import i18nService from "../../services/i18nService";
import LanguageSelect from "../LanguageSelect";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

// Fills the `{{language}}` slot of a translated sentence with a styled span,
// so the sentence stays whole in the catalog.
const withLanguageName = (template, name) => {
  const marker = "{{language}}";
  const idx = template.indexOf(marker);
  if (idx === -1) return template;
  return (
    <>
      {template.slice(0, idx)}
      <span className="font-medium text-gray-700">{name}</span>
      {template.slice(idx + marker.length)}
    </>
  );
};

/**
 * Settings → Language. The user either picks a language or leaves it on
 * "inherit", in which case the team default, then the organization default,
 * then the browser decide. The backend reports which one won so we can say
 * so here.
 */
const LanguageTab = () => {
  const { t } = useTranslation("settings");
  const { user, refreshUser } = useContext(AuthContext);
  const sourceLabel = {
    user: t("language.source.user"),
    team: t("language.source.team"),
    organization: t("language.source.organization"),
    browser: t("language.source.browser"),
    platform: t("language.source.platform"),
  };
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSelected(user?.locale || "");
  }, [user?.locale]);

  const { data: resolved } = useQuery({
    queryKey: ["i18n", "locale", user?.id],
    queryFn: i18nService.getMyLocale,
    enabled: !!user,
  });

  const languages = queryClient.getQueryData(["i18n", "languages"]) || [];
  const nameOf = (code) =>
    languages.find((l) => l.code === code)?.name || code || "";

  const saveMutation = useMutation({
    mutationFn: (code) =>
      usersService.updateCurrentUser({ locale: code === "" ? null : code }),
    onSuccess: () => {
      setError("");
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      queryClient.invalidateQueries({ queryKey: ["i18n", "locale"] });
      if (refreshUser) refreshUser();
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => {
      setSaved(false);
      setError(formatError(err));
      setSelected(user?.locale || "");
    },
  });

  const dirty = (selected || "") !== (user?.locale || "");

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
        <Globe className="w-5 h-5 text-purple-600" />
        {t("language.title")}
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        {t("language.description")}
      </p>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-3 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {saved && (
        <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-2xl px-3 py-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{t("language.saved")}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="user-locale"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {t("language.display_language")}
          </label>
          <LanguageSelect
            id="user-locale"
            value={selected}
            onChange={setSelected}
            inheritLabel={t("language.inherit_option")}
          />
        </div>

        {resolved && (
          <p className="text-xs text-gray-500">
            {withLanguageName(
              t("language.currently_showing", {
                source: sourceLabel[resolved.source] || resolved.source,
              }),
              nameOf(resolved.locale)
            )}
            {resolved.team_locale && resolved.source !== "team" && (
              <>
                {" "}
                {t("language.team_default", {
                  language: nameOf(resolved.team_locale),
                })}
              </>
            )}
            {resolved.organization_locale &&
              resolved.source !== "organization" && (
                <>
                  {" "}
                  {t("language.organization_default", {
                    language: nameOf(resolved.organization_locale),
                  })}
                </>
              )}
          </p>
        )}

        {resolved && (
          <div
            className={
              resolved.translation_enabled
                ? "text-xs rounded-2xl px-3 py-2 bg-green-50 border border-green-200 text-green-800"
                : "text-xs rounded-2xl px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800"
            }
          >
            {resolved.translation_enabled
              ? t("language.engine_on")
              : t("language.engine_off")}
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => saveMutation.mutate(selected)}
            disabled={!dirty || saveMutation.isPending}
            className="px-4 py-2 org-bg-primary org-btn text-white rounded-full text-sm transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? t("common:actions.saving") : t("common:actions.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageTab;
